import React, { useState, useEffect } from 'react'
import { api } from '../../api/client.js'

function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const buffer = new ArrayBuffer(rawData.length)
  const outputArray = new Uint8Array(buffer)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return buffer
}

export const BrowserNotificationPrompt: React.FC = () => {
  const [showPrompt, setShowPrompt] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window) || !('serviceWorker' in navigator)) {
      return
    }

    const dismissed = sessionStorage.getItem('kc3_dismiss_push_prompt')
    if (Notification.permission === 'default' && !dismissed) {
      setShowPrompt(true)
    }
  }, [])

  const handleEnable = async () => {
    try {
      setLoading(true)
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        setShowPrompt(false)
        return
      }

      // Register service worker if not already registered
      const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' })
      await navigator.serviceWorker.ready

      // Fetch VAPID public key
      const { publicKey } = await api.get<{ publicKey: string }>('/notifications/push/vapid-public-key')
      if (!publicKey) {
        setShowPrompt(false)
        return
      }

      // Subscribe to PushManager
      const applicationServerKey = urlBase64ToUint8Array(publicKey)
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      })

      const subJson = subscription.toJSON()
      if (subJson.endpoint && subJson.keys?.p256dh && subJson.keys?.auth) {
        await api.post('/notifications/push/subscribe', {
          endpoint: subJson.endpoint,
          keys: {
            p256dh: subJson.keys.p256dh,
            auth: subJson.keys.auth,
          },
          userAgent: navigator.userAgent,
        })
      }

      setShowPrompt(false)
    } catch (err) {
      console.warn('Failed to register browser push notifications:', err)
      setShowPrompt(false)
    } finally {
      setLoading(false)
    }
  }

  const handleDismiss = () => {
    try {
      sessionStorage.setItem('kc3_dismiss_push_prompt', '1')
    } catch {
      // ignore
    }
    setShowPrompt(false)
  }

  if (!showPrompt) return null

  return (
    <div
      role="region"
      aria-label="Browser Notification Prompt"
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 9999,
        backgroundColor: '#1e293b',
        color: '#f8fafc',
        padding: '16px 20px',
        borderRadius: 8,
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.4), 0 8px 10px -6px rgba(0, 0, 0, 0.3)',
        maxWidth: 360,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        border: '1px solid #334155',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <span style={{ fontSize: 24 }}>🔔</span>
        <div>
          <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#f1f5f9' }}>
            Enable Browser Notifications
          </h4>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#94a3b8', lineHeight: 1.4 }}>
            Get instant desktop alerts for ticket assignments, replies, and campaign updates even when the window is minimized.
          </p>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
        <button
          type="button"
          onClick={handleDismiss}
          disabled={loading}
          style={{
            padding: '6px 12px',
            fontSize: 12,
            background: 'transparent',
            border: 'none',
            color: '#94a3b8',
            cursor: 'pointer',
            borderRadius: 4,
          }}
        >
          Later
        </button>
        <button
          type="button"
          onClick={handleEnable}
          disabled={loading}
          style={{
            padding: '6px 14px',
            fontSize: 12,
            fontWeight: 500,
            backgroundColor: '#2563eb',
            border: 'none',
            color: '#ffffff',
            cursor: 'pointer',
            borderRadius: 4,
          }}
        >
          {loading ? 'Enabling...' : 'Enable Notifications'}
        </button>
      </div>
    </div>
  )
}
